import React from 'react'
import Section, { Muted } from './Section'
import { Tag } from './StatusPill'
import { buyerReasonLabel, fmtClockMs, fmtDay, outcomeText, resultText, stepLabel, stepTone } from './model'

// The lead's steps in order, to the millisecond. An `inferred` step (the
// intake checks, recorded without a lead id) happened - the lead exists -
// but its time is the lead's creation, not a measurement, so it is grey and
// says so. So is the `result` step the backend adds when routing recorded no
// outcome: built from the lead's status, with no time at all.
export default function Timeline({ steps, timezone }) {
  return (
    <Section id="li-timeline" title="Timeline" subtitle="Times in ET, to the millisecond">
      {steps.length === 0 ? <Muted>No steps recorded.</Muted> : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
          {steps.map((s, i) => {
            const day = fmtDay(s.at, timezone)
            const showDay = i === 0 || day !== fmtDay(steps[i - 1].at, timezone)
            const tone = stepTone(s.outcome)
            const last = i === steps.length - 1
            const isResult = s.step === 'result'
            const outcome = isResult ? resultText(s) : outcomeText(s.outcome)
            // A result names its winner in its own text.
            const buyer = isResult ? null : s.buyer
            return (
              <li
                key={i}
                data-testid="timeline-step"
                data-inferred={s.inferred ? 'true' : 'false'}
                className={s.inferred ? 'li-step-inferred' : undefined}
                style={{ display: 'grid', gridTemplateColumns: '92px 16px minmax(0, 1fr)', gap: 10 }}
              >
                <div style={{ textAlign: 'right', paddingTop: 1 }}>
                  <div className="li-mono" style={{ color: s.inferred ? 'var(--text-muted)' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtClockMs(s.at, timezone)}
                  </div>
                  {showDay && day && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{day}</div>}
                </div>
                <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: 999, marginTop: 4, flexShrink: 0,
                    background: s.inferred ? 'transparent' : tone.fg,
                    border: `2px ${s.inferred ? 'dashed' : 'solid'} ${s.inferred ? 'var(--text-muted)' : tone.fg}`,
                  }} />
                  {!last && <span style={{ flex: 1, width: 2, background: 'var(--border)', marginTop: 2 }} />}
                </div>
                <div style={{ paddingBottom: last ? 0 : 14, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: s.inferred ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                      {stepLabel(s.step)}
                    </span>
                    {outcome && (s.inferred
                      ? <span style={{ fontSize: 12 }}>{outcome}</span>
                      : <Tag tone={tone}>{outcome}</Tag>)}
                    {s.inferred && <span style={{ fontSize: 11, fontStyle: 'italic' }}>not measured</span>}
                  </div>
                  {(buyer || s.detail) && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflowWrap: 'anywhere' }}>
                      {buyer && <span style={{ fontWeight: 600 }}>{buyer}</span>}
                      {buyer && s.detail && ' · '}
                      {buyerReasonLabel(s.detail)}
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </Section>
  )
}
